import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayButton, DayPicker } from 'react-day-picker';

import { cn } from '../lib/utils.js';
import { buttonVariants } from './button.js';

/* eslint-disable i18next/no-literal-string -- classNames are CSS, not user-facing strings */

function Calendar({
  className,
  classNames,
  captionLayout = 'label',
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={false}
      className={cn('bg-background p-3', className)}
      captionLayout={captionLayout}
      classNames={{
        root: 'w-fit',
        months: 'relative flex flex-col gap-6 sm:flex-row',
        month: 'flex w-full flex-col gap-4',
        nav: 'absolute inset-x-0 top-0 flex w-full items-center justify-between',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-9 p-0 select-none aria-disabled:opacity-50',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-9 p-0 select-none aria-disabled:opacity-50',
        ),
        month_caption: 'flex h-9 w-full items-center justify-center',
        caption_label: 'select-none text-sm font-medium',
        table: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-9 select-none text-center text-[0.75rem] font-medium',
        week: 'mt-0.5 flex w-full',
        day: 'relative h-9 w-9 select-none p-0 text-center',
        range_start: 'bg-primary/10 rounded-l-md',
        range_middle: 'bg-primary/10',
        range_end: 'bg-primary/10 rounded-r-md',
        today: '[&_button]:font-semibold [&_button]:text-primary',
        outside: '[&_button]:text-muted-foreground/40',
        disabled: '[&_button]:text-muted-foreground/50 [&_button]:pointer-events-none',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        // eslint-disable-next-line react/no-unstable-nested-components -- react-day-picker requires inline component override
        Chevron: ({ orientation, ...chevronProps }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className="size-4" {...chevronProps} />;
          }
          return <ChevronRightIcon className="size-4" {...chevronProps} />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

function getCalendarDayVariant(modifiers: Record<string, boolean>) {
  const isEndpoint = modifiers.range_start || modifiers.range_end;
  if (isEndpoint) return 'bg-primary text-primary-foreground hover:bg-primary/90';
  if (modifiers.range_middle) return 'bg-transparent text-foreground rounded-none';
  if (modifiers.selected) return 'bg-primary text-primary-foreground hover:bg-primary/90';
  return 'hover:bg-accent hover:text-accent-foreground';
}

function CalendarDayButton({
  className,
  day: _day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-md text-[13px] font-normal transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        getCalendarDayVariant(modifiers),
        className,
      )}
      {...props}
    />
  );
}

/* eslint-enable i18next/no-literal-string */

export { Calendar, CalendarDayButton };
